<template>
  <v-col cols="12" sm="6" md="6" lg="3">
    <v-card style="cursor: pointer" @click="dialog = true">
      <v-progress-linear
        color="primary"
        indeterminate
        v-if="$apollo.loading"
      ></v-progress-linear>
      <v-toolbar flat dense>
        <span class="text-subtitle-1">Inventory</span>
      </v-toolbar>
      <div class="stat-data">
        <div class="main-stat" :class="getDifferenceTextClass()">
          <v-icon
            :color="getDifferenceIconColor()"
            v-if="totalDifference < 0"
            small
            class="mr-1"
            >mdi-arrow-down-thick</v-icon
          >
          <v-icon
            :color="getDifferenceIconColor()"
            v-if="totalDifference > 0"
            small
            class="mr-1"
            >mdi-arrow-up-thick</v-icon
          >
          {{ totalDifference }}
        </div>
        <span class="stat-data-desc">
          <span class="count-text">{{ totalRetained }}</span>
          <span class="label-text">Retained</span> /
          <span class="count-text">{{ totalResolved }}</span>
          <span class="label-text">Resolved</span>
        </span>
        <span class="stat-data-breakdown">
          ({{ totalSettled }} Settled, {{ totalWithdrawnOrNoFee }} Other)
        </span>
      </div>
    </v-card>

    <v-dialog v-model="dialog" max-width="560">
      <v-card>
        <v-toolbar flat dense>
          <span class="text-subtitle-1">Inventory by Market</span>
          <v-spacer />
          <v-btn icon small @click="dialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-progress-linear
          color="primary"
          indeterminate
          v-if="$apollo.queries.marketComparison.loading"
        ></v-progress-linear>

        <v-simple-table dense class="market-table">
          <thead>
            <tr>
              <th class="text-left">Market</th>
              <th class="text-right">Retained</th>
              <th class="text-right">Resolved</th>
              <th class="text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in marketRows" :key="row.market">
              <td>{{ row.market }}</td>
              <td class="text-right">{{ row.retained }}</td>
              <td class="text-right">
                {{ row.resolved }}
                <span class="market-breakdown"
                  >({{ row.settled }} Settled, {{ row.other }} Other)</span
                >
              </td>
              <td class="text-right" :class="netClass(row)">
                {{ row.retained - row.resolved }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import gql from "graphql-tag";
import { mapGetters } from "vuex";

export default {
  apollo: {
    comparison: {
      query: gql`
        query OrgHomeStatsInventory($startDate: String!, $endDate: String!) {
          comparison: totalInventoryComparisonInPeriod(
            startDate: $startDate
            endDate: $endDate
          ) {
            totalRetainedCases
            totalSettledCases
            totalWithdrawnOrNoFeeCases
            totalResolvedCases
          }
        }
      `,
      variables() {
        return {
          startDate: this.startDate,
          endDate: this.endDate
        };
      },
      skip() {
        return this.startDate === null || this.endDate === null;
      }
    },
    marketComparison: {
      query: gql`
        query InventoryByMarket($startDate: String!, $endDate: String!) {
          marketComparison: inventoryComparisonByMarket(
            startDate: $startDate
            endDate: $endDate
          ) {
            market
            retained
            settled
            other
            resolved
          }
        }
      `,
      variables() {
        return {
          startDate: this.startDate,
          endDate: this.endDate
        };
      },
      skip() {
        return !this.dialog || this.startDate === null || this.endDate === null;
      }
    }
  },
  data() {
    return {
      dialog: false
    };
  },
  computed: {
    ...mapGetters(["currentUser", "startDate", "endDate"]),
    totalRetained() {
      if (!this.comparison) return 0;
      return this.comparison.totalRetainedCases;
    },
    totalSettled() {
      if (!this.comparison) return 0;
      return this.comparison.totalSettledCases;
    },
    totalWithdrawnOrNoFee() {
      if (!this.comparison) return 0;
      return this.comparison.totalWithdrawnOrNoFeeCases;
    },
    totalResolved() {
      if (!this.comparison) return 0;
      return this.comparison.totalResolvedCases;
    },
    totalDifference() {
      return this.totalRetained - this.totalResolved;
    },
    marketRows() {
      return this.marketComparison || [];
    }
  },
  methods: {
    getDifferenceTextClass() {
      if (this.totalDifference > 0) return "success--text";
      if (this.totalDifference < 0) return "error--text";
      return "";
    },
    getDifferenceIconColor() {
      if (this.totalDifference > 0) return "success";
      if (this.totalDifference < 0) return "error";
      return "grey";
    },
    netClass(row) {
      const net = row.retained - row.resolved;
      if (net > 0) return "success--text";
      if (net < 0) return "error--text";
      return "";
    }
  }
};
</script>

<style lang="scss">
// Enhanced typography for better readability
.main-stat {
  font-size: 2rem !important;
  font-weight: 600 !important;
  line-height: 1.2;
  display: flex;
  align-items: center;
}

.count-text {
  font-weight: 600 !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

.label-text {
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.75) !important;
  margin-left: 2px;
  margin-right: 4px;
}

.stat-data-desc {
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.75) !important;
}

.stat-data-breakdown {
  display: block;
  font-size: 0.75rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.54);
  margin-top: 2px;
}

.market-table {
  td,
  th {
    padding-top: 8px !important;
    padding-bottom: 8px !important;
  }
}

.market-breakdown {
  font-size: 0.7rem;
  color: rgba(0, 0, 0, 0.54);
  margin-left: 4px;
}
</style>
